import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { createNewChat, setUnreadCount } from 'actions/supportAssistant/supportAssistantActions'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import TextButton from 'LuxKit/Button/TextButton'
import IconButton from 'LuxKit/Button/IconButton'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import LineArrowDownIcon from 'LuxKit/Icons/line/LineArrowDownIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Group from 'components/utils/Group'
import { rem } from 'polished'
import styled from 'styled-components'
import cn from 'clsx'
import HeadData from 'components/Common/HeadData'
import MessagesListContainer from 'supportAssistant/components/Chat/MessagesListContainer'
import useChatState from 'supportAssistant/hooks/useChatState'
import useTabVisibility from 'hooks/useTabVisibility'
import { selectCurrentChat } from 'supportAssistant/selectors/selectCurrentChat'
import { getUnreadCount } from 'selectors/supportAssistantSelectors'
import Agent from '../Support/Agent'
import MetaComponents from './MessageMeta/MetaComponents'
import ChatEmptyState from 'supportAssistant/components/Chat/ChatEmptyState'
import { set } from 'lib/storage/isomorphicLocalStorage'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyFeatureFlags } from 'constants/optimizely'

const SCROLL_THRESHOLD = 100

const ScrollableMessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  position: relative;
  padding-right: ${rem(8)};

  &::-webkit-scrollbar {
    width: ${rem(6)};
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.palette.neutral.default.three};
    border-radius: ${rem(3)};
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &.scrolling::-webkit-scrollbar-thumb {
    opacity: 1;
  }

  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
  transition: scrollbar-color 0.3s ease;

  &.scrolling {
    scrollbar-color: ${props => props.theme.palette.neutral.default.three} transparent;
  }
`

const ScrollToBottomButton = styled.div`
  position: sticky;
  bottom: ${rem(16)};
  display: flex;
  justify-content: center;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.2s ease;

  &.visible {
    opacity: 1;
    pointer-events: auto;
  }
`

const StyledIconButton = styled(IconButton)`
  box-shadow: 0 ${rem(4)} ${rem(12)} rgba(15, 23, 42, 0.15), 0 ${rem(2)} ${rem(6)} rgba(15, 23, 42, 0.1);
`

const StartNewChatContainer = styled(Group)`
  padding-left: ${rem(32)};
`

const ActionsSkeleton = styled(LoadingBox)`
  align-self: start;
  height: ${rem(40)};
  margin-top: ${rem(12)};
  margin-left: ${rem(32)};
`

const ScrollTo = styled.div`
  padding-bottom: ${rem(24)};
`

export interface ChatContentProps {
  compact?: boolean
}

function ChatContent(props: ChatContentProps) {
  const { compact = false } = props

  const [messages, typing, agentTyping, fetchingChatMessages] = useChatState()
  const hasUserMessage = useMemo(() => messages.some(m => m.sender === 'user'), [messages])
  const isEmptyState = !fetchingChatMessages && !hasUserMessage
  const dispatch = useAppDispatch()
  const isTabVisible = useTabVisibility()

  const currentChat = useAppSelector(state => selectCurrentChat(state))
  const unreadMessageCount = useAppSelector(getUnreadCount)

  const lastMessage = messages[messages.length - 1]
  const isLastMessageByUser = lastMessage?.sender === 'user'

  const showChatActions = !isEmptyState && !fetchingChatMessages && lastMessage && currentChat?.status !== 'CLOSED' && lastMessage.sender === 'chatbot'

  const isLESAConditionalBubbleEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.opexLESAConditionalBubble)

  // Document title management for page layout
  const [showDocumentTitleMessage, setShowDocumentTitleMessage] = useState(false)
  const lastSeenMessageForTitle = useRef(lastMessage)

  useEffect(() => {
    if (!compact && !isTabVisible && lastSeenMessageForTitle?.current?.id !== lastMessage?.id) {
      setShowDocumentTitleMessage(true)
      lastSeenMessageForTitle.current = lastMessage
    }
  }, [isTabVisible, lastMessage, messages.length, compact])

  useEffect(() => {
    if (!compact && (isLastMessageByUser || isTabVisible)) {
      setShowDocumentTitleMessage(false)
    }
  }, [isLastMessageByUser, isTabVisible, messages, compact])

  const handleNewChatClick = useCallback(() => {
    dispatch(createNewChat())
  }, [dispatch])

  const scrollRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [isAtBottom, setIsAtBottom] = useState(true)
  const [isScrolling, setIsScrolling] = useState(false)
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const scrollToBottom = useCallback(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
    setIsAtBottom(true)
  }, [])

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current
    if (!container) return

    const { scrollTop, scrollHeight, clientHeight } = container
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight
    setIsAtBottom(distanceFromBottom < SCROLL_THRESHOLD)

    setIsScrolling(true)
    clearTimeout(scrollTimeoutRef.current)
    scrollTimeoutRef.current = setTimeout(() => setIsScrolling(false), 500)
  }, [])

  useEffect(() => {
    scrollToBottom()
  }, [messages.length, typing, agentTyping, scrollToBottom])

  const lastSeenMessage = useRef(lastMessage)
  const hasRenderedMessages = useRef(false)
  const LAST_SEEN_PREFIX = 'lastSeenAt-'

  // Reset the rendered flag when chat changes
  useEffect(() => {
    hasRenderedMessages.current = false
  }, [currentChat?.id])

  useEffect(() => {
    if (!isTabVisible && lastSeenMessage?.current?.id !== lastMessage?.id) {
      lastSeenMessage.current = lastMessage
    }
  }, [isTabVisible, lastMessage, messages.length])

  // Mark messages as read when messages are rendered and the tab is visible
  const handleMessagesRendered = useCallback(() => {
    hasRenderedMessages.current = true
    if (currentChat?.id && isTabVisible && isLESAConditionalBubbleEnabled) {
      set(`${LAST_SEEN_PREFIX}${currentChat.id}`, new Date().toISOString())
      dispatch(setUnreadCount(0))
    }
  }, [currentChat?.id, isTabVisible, isLESAConditionalBubbleEnabled, dispatch])

  // Mark messages as read when messages are already rendered and the tab becomes visible
  useEffect(() => {
    if (isTabVisible && hasRenderedMessages.current && currentChat?.id && !isEmptyState && isLESAConditionalBubbleEnabled) {
      set(`${LAST_SEEN_PREFIX}${currentChat.id}`, new Date().toISOString())
      dispatch(setUnreadCount(0))
    }
  }, [isTabVisible, currentChat?.id, isEmptyState, isLESAConditionalBubbleEnabled, dispatch])

  const emptyStateClassName = compact ? 'chat-empty-state-container' : undefined

  // For compact mode (chat widget), render with original behavior including empty state
  if (compact && isEmptyState) {
    return (
      <Group
        direction="vertical"
        verticalAlign="center"
        gap={16}
        fullHeight
      >
        {fetchingChatMessages && <ActionsSkeleton />}
        {!fetchingChatMessages && <ChatEmptyState
          isTyping={typing}
          actions={messages[0]?.meta?.content}
          payload={messages[0]?.meta?.payload}
          className={emptyStateClassName}
        />}
      </Group>
    )
  }

  const shouldShowHeadData = !compact && isLESAConditionalBubbleEnabled && showDocumentTitleMessage && unreadMessageCount > 0

  // Render shared layout for both compact and full-page modes
  return (
    <>
      {shouldShowHeadData && (
        <HeadData defer={false} title={`(${unreadMessageCount}) Luxury Escapes`} />
      )}

      <ScrollableMessagesContainer ref={scrollContainerRef} onScroll={handleScroll} className={cn({ scrolling: isScrolling })}>
        <Group direction="vertical" gap={16}>
          {fetchingChatMessages && <ActionsSkeleton />}

          {!fetchingChatMessages && (
            <>
              <MessagesListContainer
                loadingMessages={fetchingChatMessages}
                messages={messages}
                isTyping={typing}
                isAgentTyping={agentTyping}
                onMessagesRendered={handleMessagesRendered}
                compact={compact}
              />
              {showChatActions && lastMessage?.meta?.content && (
                <MetaComponents
                  metaContent={lastMessage.meta.content}
                  payload={lastMessage?.meta?.payload}
                />
              )}
            </>
          )}

          {!fetchingChatMessages && currentChat?.status === 'CLOSED' && (
            <StartNewChatContainer fullWidth horizontalAlign="start" direction="vertical" gap={8}>
              <BodyTextBlock variant="small">
                This chat is timed out. Please start a new chat.
              </BodyTextBlock>

              <TextButton
                kind="secondary"
                variant="dark"
                onClick={handleNewChatClick}
                startIcon={<LinePlusIcon />}
              >
                Start new chat
              </TextButton>
            </StartNewChatContainer>
          )}

          <Agent loadingMessages={fetchingChatMessages} />
          <ScrollTo ref={scrollRef} />
        </Group>
        <ScrollToBottomButton className={cn({ visible: !isAtBottom })}>
          <StyledIconButton
            kind="secondary"
            variant="dark"
            shape="circle"
            size="medium"
            onClick={scrollToBottom}
            aria-label="Scroll to bottom"
          >
            <LineArrowDownIcon />
          </StyledIconButton>
        </ScrollToBottomButton>
      </ScrollableMessagesContainer>
    </>
  )
}

export default ChatContent
